import * as React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import colors from 'components/utils/colors'

// --- Icons ---
import { ChevronDownIcon } from '@tiptap-ui/components/tiptap-icons/chevron-down-icon'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

// --- Tiptap UI ---
import { HeadingButton } from '@tiptap-ui/components/tiptap-ui/heading-button'
import { useHeadingDropdownMenu } from '@tiptap-ui/components/tiptap-ui/heading-dropdown-menu'

import { Button, ButtonGroup } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@tiptap-ui/components/tiptap-ui-primitive/dropdown-menu'
import { Card, CardBody } from '@tiptap-ui/components/tiptap-ui-primitive/card'

const ResetButton = styled(Button)`
  &&& {
    height: auto;
    justify-content: left;

    &:hover:not([disabled]) {
      background: ${colors.bgPrimaryHover};
    }
  }
`

/**
 * Dropdown menu component for selecting heading levels in a Tiptap editor.
 *
 * For custom dropdown implementations, use the `useHeadingDropdownMenu` hook instead.
 */
export const HeadingDropdownMenu = React.forwardRef((
  {
    editor: providedEditor,
    levels = [1, 2, 3, 4, 5, 6],
    hideWhenUnavailable = false,
    portal = false,
    onOpenChange,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = React.useState(false)
  const { isVisible, isActive, canToggle, Icon } = useHeadingDropdownMenu({
    editor,
    levels,
    hideWhenUnavailable,
  })

  const canReset = canToggle && isActive

  const handleReset = React.useCallback(() => {
    if (!editor || !canReset) return
    editor
      .chain()
      .focus()
      .clearNodes()
      .unsetAllMarks()
      .setParagraph()
      .run()
    setIsOpen(false)
  }, [editor, canReset])

  const handleOpenChange = React.useCallback((open) => {
    if (!editor || !canToggle) return
    setIsOpen(open)
    onOpenChange?.(open)
  }, [canToggle, editor, onOpenChange])

  if (!isVisible) {
    return null
  }

  const resetButtonLabel = 'body text'

  return (
    <DropdownMenu modal open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          data-style='ghost'
          data-active-state={isActive ? 'on' : 'off'}
          role='button'
          tabIndex={-1}
          disabled={!canToggle}
          data-disabled={!canToggle}
          aria-label='Format text as heading'
          aria-pressed={isActive}
          tooltip='Heading'
          {...buttonProps}
          ref={ref}
        >
          <Icon className='tiptap-button-icon' />
          <ChevronDownIcon className='tiptap-button-dropdown-small' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' portal={portal}>
        <Card>
          <CardBody>
            <ButtonGroup>
              {levels.map((level) => (
                <DropdownMenuItem key={`heading-${level}`} asChild>
                  <HeadingButton
                    editor={editor}
                    level={level}
                    text={`Header ${level}`}
                    showTooltip={false}
                  />
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem key='reset' asChild>
                <ResetButton
                  type='button'
                  data-style='ghost'
                  role='button'
                  tabIndex={-1}
                  disabled={!canReset}
                  data-disabled={!canReset}
                  onClick={handleReset}
                  ref={ref}
                >
                  {resetButtonLabel}
                </ResetButton>
              </DropdownMenuItem>
            </ButtonGroup>
          </CardBody>
        </Card>
      </DropdownMenuContent>
    </DropdownMenu>
  )
})

HeadingDropdownMenu.displayName = 'HeadingDropdownMenu'

HeadingDropdownMenu.propTypes = {
  editor: PropTypes.object,
  levels: PropTypes.arrayOf(PropTypes.number),
  hideWhenUnavailable: PropTypes.bool,
  portal: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
}

export default HeadingDropdownMenu
